import { useRecoilValue } from "recoil";
import { pubState } from "../../../recoil/pub.atom";
import { useEffect, useState, useCallback } from "react";
import {
  getNowPubSeason,
  getRunnerRanking,
  NowSeasonRes,
  SellerRankingDto,
} from "../../../api/ranking";

// 상수 정의
const DEFAULT_ITEMS = {
  PAGE_0: ["season", "month", "week"],
  PAGE_1: ["entry", "itm", "trophy"],
} as const;

const ITEM_CONFIG = {
  season: { title: "매장 랭킹", info: "", unit: "R", hasGap: true },
  month: { title: "월간 랭킹", info: "", unit: "R", hasGap: true },
  week: { title: "주간 랭킹", info: "", unit: "R", hasGap: true },
  entry: {
    title: "엔트리 왕",
    info: "·    엔트리 횟수",
    unit: "회",
    hasGap: false,
  },
  itm: {
    title: "ITM 마스터",
    info: "·    ITM 구간 진입 횟수",
    unit: "회",
    hasGap: false,
  },
  trophy: {
    title: "챔피언 헌터",
    info: "·    토너먼트 우승 횟수",
    unit: "회",
    hasGap: false,
  },
} as const;

export const useRankingBoard = () => {
  const pub = useRecoilValue(pubState);
  const [page, setPage] = useState(0);
  const [aniPage, setAniPage] = useState(0);
  const [currentBgIndex, setCurrentBgIndex] = useState(0);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [nowSeason, setNowSeason] = useState<NowSeasonRes | null>(null);

  const isNotAutoNextPage =
    localStorage.getItem("isNotAutoNextPage") === "true";

  const isNotAutoPage2 = localStorage.getItem("isNotAutoPage2") === "true";

  const [firstBoxData, setFirstBoxData] = useState<SellerRankingDto[]>([]);
  const [secondBoxData, setSecondBoxData] = useState<SellerRankingDto[]>([]);
  const [thirdBoxData, setThirdBoxData] = useState<SellerRankingDto[]>([]);

  // 유틸리티 함수들
  const getCurrentItems = useCallback(() => {
    const defaultItems =
      page === 0 ? DEFAULT_ITEMS.PAGE_0 : DEFAULT_ITEMS.PAGE_1;
    return [
      localStorage.getItem(`item${page === 0 ? 1 : 4}`) || defaultItems[0],
      localStorage.getItem(`item${page === 0 ? 2 : 5}`) || defaultItems[1],
      localStorage.getItem(`item${page === 0 ? 3 : 6}`) || defaultItems[2],
    ];
  }, [page]);

  const getItemConfig = (item: string) =>
    ITEM_CONFIG[item as keyof typeof ITEM_CONFIG];

  // 페이지 관련 함수들
  const setNextPage = useCallback(() => setAniPage(page), [page]);
  const setRealNextPage = useCallback(
    () => setPage((prev) => (prev === 0 ? 1 : 0)),
    [],
  );

  // API 호출 함수들
  const getNowSeasons = useCallback(async () => {
    if (!pub) return;
    const response = await getNowPubSeason();
    setNowSeason(response);
  }, [pub]);

  const getRankingData = useCallback(async () => {
    if (!pub) {
      setDataLoaded(true);
      return;
    }
    setDataLoaded(false);

    const [currentItem1, currentItem2, currentItem3] = getCurrentItems();

    const response = await getRunnerRanking(
      pub?.id,
      currentItem1,
      currentItem2,
      currentItem3,
    );

    const mapItemData = (items: any[], itemType: string) =>
      items.map((item) => ({
        ...item,
        score: item.score,
        item: itemType,
      }));

    setFirstBoxData(mapItemData(response.item1, currentItem1));
    setSecondBoxData(mapItemData(response.item2, currentItem2));
    setThirdBoxData(mapItemData(response.item3, currentItem3));
    setNextPage();
    setDataLoaded(true);
  }, [pub, getCurrentItems, setNextPage]);

  // Getter 함수들 (현재 애니메이션 페이지 기준)
  const getBoxName = (boxIndex: number) => {
    const items = getCurrentItems();
    return getItemConfig(items[boxIndex])?.title;
  };

  const getBoxInfo = (boxIndex: number) => {
    const items = getCurrentItems();
    return getItemConfig(items[boxIndex])?.info || "";
  };

  const getScoreUnit = (item: string) => getItemConfig(item)?.unit || "회";
  const getScore = (score: number) => score.toLocaleString();
  const isGapWidth = (item: string) => getItemConfig(item)?.hasGap || false;

  // useEffect 훅들
  useEffect(() => {
    getNowSeasons();
  }, [getNowSeasons]);

  useEffect(() => {
    getRankingData();
  }, [getRankingData]);

  useEffect(() => {
    if (!isNotAutoNextPage && !isNotAutoPage2) {
      const interval = setInterval(setRealNextPage, 15000);
      return () => clearInterval(interval);
    }
  }, [isNotAutoNextPage, isNotAutoPage2, setRealNextPage]);

  useEffect(() => {
    const bgInterval = setInterval(() => {
      setCurrentBgIndex((prev) => (prev + 1) % 3);
    }, 1800);
    return () => clearInterval(bgInterval);
  }, []);

  // 레거시 호환성을 위한 개별 함수들 (기존 컴포넌트에서 사용)
  const getFirstBoxName = () => getBoxName(0);
  const getSecondBoxName = () => getBoxName(1);
  const getThirdBoxName = () => getBoxName(2);

  const getFirstBoxInfo = () => getBoxInfo(0);
  const getSecondBoxInfo = () => getBoxInfo(1);
  const getThirdBoxInfo = () => getBoxInfo(2);

  return {
    // 상태
    pub,
    page,
    aniPage,
    currentBgIndex,
    dataLoaded,
    nowSeason,
    isNotAutoNextPage,
    isNotAutoPage2,

    // 데이터
    firstBoxData,
    secondBoxData,
    thirdBoxData,

    // 유틸리티 함수들
    getScore,
    getScoreUnit,
    isGapWidth,

    // 페이지 관련
    setRealNextPage,

    // 박스별 정보 (레거시 호환)
    getFirstBoxName,
    getSecondBoxName,
    getThirdBoxName,
    getFirstBoxInfo,
    getSecondBoxInfo,
    getThirdBoxInfo,
  };
};
